import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { getEffectiveSlotOccupancy, getFormulaById, MAX_PLAYERS_PER_SLOT } from '../config/formulas';

const SESSIONS_COLLECTION = 'sessions';
const PARTICIPANTS_COLLECTION = 'participants';
const BOOKINGS_COLLECTION = 'bookings';
const CONTACTS_COLLECTION = 'contacts';
const SETTINGS_COLLECTION = 'settings';
const BOOKING_SETTINGS_DOC = 'booking';

// ============================================
// INITIALISATION
// ============================================

export async function initializeData() {
  // Plus d'initialisation automatique.
  // Les sessions sont créées par l'admin quand il ouvre un jour ou un créneau.
  console.log("LaserBay prêt.");
  return true;
}

// ============================================
// SESSIONS (créneaux de 30 min)
// ============================================

export async function getSessions() {
  try {
    const sessionsCol = collection(db, SESSIONS_COLLECTION);
    const snapshot = await getDocs(sessionsCol);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt ? docSnap.data().createdAt.toDate() : null
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des sessions:", error);
    throw error;
  }
}

export async function createSession(sessionData) {
  try {
    const dataWithTimestamp = {
      ...sessionData,
      isOpen: sessionData.isOpen !== undefined ? sessionData.isOpen : false,
      bookingIds: sessionData.bookingIds || [],
      createdAt: serverTimestamp()
    };
    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), dataWithTimestamp);
    const newDoc = await getDoc(docRef);
    return { id: newDoc.id, ...newDoc.data() };
  } catch (error) {
    console.error("Erreur lors de la création de la session:", error);
    throw error;
  }
}

// ============================================
// BOOKINGS (réservations avec formule)
// ============================================

// Créer une réservation complète (booking + mise à jour des sessions)
// ============================================
// DURÉE DE BLOCAGE D'UNE RÉSERVATION EN ATTENTE DE PAIEMENT
// ============================================
// Quand un client part payer sur Stripe, sa place est bloquée
// temporairement. S'il ne paie pas dans ce délai, la réservation
// est supprimée automatiquement et la place se relibère.
export const PENDING_PAYMENT_MINUTES = 20;

export async function createBooking(bookingData) {
  try {
    // Calculer l'occupation effective du créneau :
    // - Formule privative (groupe/événement) → on bloque les 10 places
    // - Formule individuelle → on prend le nombre réel de participants
    const formula = getFormulaById(bookingData.formulaId);
    const slotOccupancy = getEffectiveSlotOccupancy(formula, bookingData.nbParticipants);
    const isPrivative = slotOccupancy === MAX_PLAYERS_PER_SLOT && formula?.category !== 'individual';

    // Réservation en attente de paiement ?
    // → uniquement si le client part payer en ligne par carte (Stripe)
    const isPendingPayment = bookingData.status === 'pending_payment';

    // 1. Créer le document booking (en stockant l'info pour traçabilité)
    const booking = {
      ...bookingData,
      isPrivative: isPrivative, // utile pour l'affichage et la migration future
      slotOccupancy: slotOccupancy, // place réelle prise sur le créneau
      createdAt: serverTimestamp()
    };

    // Date d'expiration : la place est bloquée temporairement
    if (isPendingPayment) {
      booking.expiresAt = new Date(Date.now() + PENDING_PAYMENT_MINUTES * 60 * 1000);
    }
    
    const bookingRef = await addDoc(collection(db, BOOKINGS_COLLECTION), booking);
    const bookingId = bookingRef.id;
    
    // 2. Mettre à jour chaque session concernée par cette réservation
    for (const slotTime of bookingData.slots) {
      // Chercher la session pour cette date et heure
      const sessionsCol = collection(db, SESSIONS_COLLECTION);
      const q = query(
        sessionsCol, 
        where("date", "==", bookingData.date), 
        where("time", "==", slotTime)
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // La session n'existe pas encore, la créer
        await addDoc(collection(db, SESSIONS_COLLECTION), {
          date: bookingData.date,
          time: slotTime,
          isBlocked: false,
          blockReason: '',
          participantCount: slotOccupancy, // ← effectif (10 si privative)
          paidCount: bookingData.isPaid ? slotOccupancy : 0,
          bookingIds: [bookingId],
          createdAt: serverTimestamp()
        });
      } else {
        // Mettre à jour la session existante
        const sessionDoc = snapshot.docs[0];
        const sessionData = sessionDoc.data();
        await updateDoc(doc(db, SESSIONS_COLLECTION, sessionDoc.id), {
          participantCount: (sessionData.participantCount || 0) + slotOccupancy, // ← effectif
          paidCount: bookingData.isPaid 
            ? (sessionData.paidCount || 0) + slotOccupancy 
            : (sessionData.paidCount || 0),
          bookingIds: [...(sessionData.bookingIds || []), bookingId]
        });
      }
    }
    
    // 3. Retourner le booking complet
    const newBooking = await getDoc(bookingRef);
    return {
      id: newBooking.id,
      ...newBooking.data()
    };
  } catch (error) {
    console.error("Erreur lors de la création de la réservation:", error);
    throw error;
  }
}

// ============================================
// NETTOYAGE DES RÉSERVATIONS NON PAYÉES EXPIRÉES
// ============================================
// Filet de sécurité côté client : si un visiteur consulte une journée,
// on purge d'abord les réservations "pending_payment" périmées afin que
// les places apparaissent immédiatement comme libres.
// (Le vrai nettoyage systématique est fait par la Cloud Function planifiée.)
export async function cleanupExpiredBookings(dateStr = null) {
  try {
    const bookingsCol = collection(db, BOOKINGS_COLLECTION);
    const q = dateStr
      ? query(bookingsCol, where("status", "==", "pending_payment"), where("date", "==", dateStr))
      : query(bookingsCol, where("status", "==", "pending_payment"));

    const snapshot = await getDocs(q);
    const now = Date.now();
    let deleted = 0;

    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (!data.expiresAt) continue;

      // expiresAt peut être un Timestamp Firestore ou une Date
      const expiry = data.expiresAt.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt);

      if (expiry.getTime() < now) {
        try {
          await deleteBooking(docSnap.id);
          deleted++;
        } catch (e) {
          console.error('Erreur suppression réservation expirée:', docSnap.id, e);
        }
      }
    }

    if (deleted > 0) {
      console.log(`${deleted} réservation(s) non payée(s) expirée(s) supprimée(s)`);
    }
    return deleted;
  } catch (error) {
    console.error("Erreur nettoyage des réservations expirées:", error);
    return 0;
  }
}

// ============================================
// RÉGLAGES DE RÉSERVATION — MODE TERRAIN
// ============================================
// Le "mode terrain" permet de lever la restriction du délai d'1h
// POUR TOUS LES CLIENTS, quand Nicolas est physiquement sur place
// (matériel déjà installé, aucune préparation nécessaire).
//
// Le réglage est stocké dans Firestore (settings/booking) et non dans
// le navigateur : c'est indispensable, car Nicolas l'active depuis son
// téléphone alors que ce sont les visiteurs, sur leurs propres appareils,
// qui doivent en bénéficier.
//
// Sécurité : le mode s'éteint automatiquement à minuit. Impossible
// d'oublier de le désactiver en repartant le soir.
// ============================================

// Retourne le mode terrain, en tenant compte de l'expiration automatique
export async function getBookingSettings() {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, BOOKING_SETTINGS_DOC);
    const snapshot = await getDoc(settingsRef);

    if (!snapshot.exists()) {
      return { fieldMode: false, fieldModeUntil: null };
    }

    const data = snapshot.data();

    // Pas activé du tout
    if (!data.fieldMode) {
      return { fieldMode: false, fieldModeUntil: null };
    }

    // Vérifier l'expiration (extinction automatique)
    if (data.fieldModeUntil) {
      const expiry = data.fieldModeUntil.toDate
        ? data.fieldModeUntil.toDate()
        : new Date(data.fieldModeUntil);

      if (expiry.getTime() < Date.now()) {
        console.log('Mode terrain expiré : désactivation automatique');
        // On nettoie en base pour que ce soit cohérent partout
        await setDoc(settingsRef, { fieldMode: false, fieldModeUntil: null }, { merge: true });
        return { fieldMode: false, fieldModeUntil: null };
      }

      return { fieldMode: true, fieldModeUntil: expiry };
    }

    return { fieldMode: true, fieldModeUntil: null };
  } catch (error) {
    console.error("Erreur lecture des réglages:", error);
    // En cas d'erreur, on retombe sur le comportement le plus prudent :
    // la restriction d'1h reste active.
    return { fieldMode: false, fieldModeUntil: null };
  }
}

// Activer / désactiver le mode terrain
// À l'activation, il expire automatiquement à la fin de la journée en cours.
export async function setFieldMode(enabled) {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, BOOKING_SETTINGS_DOC);

    let fieldModeUntil = null;
    if (enabled) {
      // Fin de la journée en cours (23h59min59s, heure locale)
      const finJournee = new Date();
      finJournee.setHours(23, 59, 59, 999);
      fieldModeUntil = finJournee;
    }

    await setDoc(settingsRef, {
      fieldMode: !!enabled,
      fieldModeUntil: fieldModeUntil,
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log(enabled ? 'Mode terrain ACTIVÉ jusqu\'à minuit' : 'Mode terrain désactivé');
    return { fieldMode: !!enabled, fieldModeUntil };
  } catch (error) {
    console.error("Erreur mise à jour du mode terrain:", error);
    throw error;
  }
}

// Récupérer toutes les réservations
export async function getBookings() {
  try {
    const bookingsCol = collection(db, BOOKINGS_COLLECTION);
    const snapshot = await getDocs(bookingsCol);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt ? docSnap.data().createdAt.toDate() : null
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des réservations:", error);
    throw error;
  }
}

// Récupérer les réservations pour une date donnée
export async function getBookingsByDate(dateStr) {
  try {
    const bookingsCol = collection(db, BOOKINGS_COLLECTION);
    const q = query(bookingsCol, where("date", "==", dateStr));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt ? docSnap.data().createdAt.toDate() : null
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des réservations:", error);
    throw error;
  }
}

// Récupérer une réservation par son ID
export async function getBookingById(bookingId) {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const bookingDoc = await getDoc(bookingRef);
    if (!bookingDoc.exists()) return null;
    return {
      id: bookingDoc.id,
      ...bookingDoc.data(),
      createdAt: bookingDoc.data().createdAt ? bookingDoc.data().createdAt.toDate() : null
    };
  } catch (error) {
    console.error("Erreur lors de la récupération de la réservation:", error);
    throw error;
  }
}

// Annuler une réservation (par le client)
export async function cancelBooking(bookingId) {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const bookingDoc = await getDoc(bookingRef);
    if (!bookingDoc.exists()) throw new Error('Réservation introuvable');
    
    const bookingData = bookingDoc.data();
    
    // Marquer comme annulée (pas supprimer — on garde l'historique)
    await updateDoc(bookingRef, {
      status: 'cancelled',
      cancelledAt: serverTimestamp()
    });

    // Libérer les créneaux dans les sessions
    if (bookingData.slots && bookingData.date) {
      const sessionsCol = collection(db, SESSIONS_COLLECTION);
      const q = query(sessionsCol, where("date", "==", bookingData.date));
      const sessionsSnap = await getDocs(q);
      
      for (const sessionDoc of sessionsSnap.docs) {
        const session = sessionDoc.data();
        if (bookingData.slots.includes(session.time)) {
          const currentBookingIds = (session.bookingIds || []).filter(id => id !== bookingId);
          // Libérer la BONNE place selon le type de formule (privative ou non)
          // On utilise slotOccupancy s'il est stocké (résas récentes), sinon on calcule à la volée
          let occupancy = bookingData.slotOccupancy;
          if (occupancy === undefined || occupancy === null) {
            const formula = getFormulaById(bookingData.formulaId);
            occupancy = getEffectiveSlotOccupancy(formula, bookingData.nbParticipants);
          }
          const currentCount = Math.max(0, (session.participantCount || 0) - occupancy);
          const currentPaid = bookingData.isPaid 
            ? Math.max(0, (session.paidCount || 0) - occupancy) 
            : (session.paidCount || 0);
          
          await updateDoc(doc(db, SESSIONS_COLLECTION, sessionDoc.id), {
            bookingIds: currentBookingIds,
            participantCount: currentCount,
            paidCount: currentPaid
          });
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de l'annulation:", error);
    throw error;
  }
}

// Supprimer une réservation (et mettre à jour les sessions)
export async function deleteBooking(bookingId) {
  try {
    // Récupérer le booking
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const bookingDoc = await getDoc(bookingRef);
    
    if (!bookingDoc.exists()) {
      throw new Error('Réservation non trouvée');
    }
    
    const bookingData = bookingDoc.data();
    
    // Mettre à jour chaque session liée
    for (const slotTime of bookingData.slots) {
      const sessionsCol = collection(db, SESSIONS_COLLECTION);
      const q = query(
        sessionsCol, 
        where("date", "==", bookingData.date), 
        where("time", "==", slotTime)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const sessionDoc = snapshot.docs[0];
        const sessionData = sessionDoc.data();
        
        // Calculer la place réellement occupée (privative = 10, sinon nb réel)
        let occupancy = bookingData.slotOccupancy;
        if (occupancy === undefined || occupancy === null) {
          const formula = getFormulaById(bookingData.formulaId);
          occupancy = getEffectiveSlotOccupancy(formula, bookingData.nbParticipants);
        }
        
        await updateDoc(doc(db, SESSIONS_COLLECTION, sessionDoc.id), {
          participantCount: Math.max(0, (sessionData.participantCount || 0) - occupancy),
          paidCount: bookingData.isPaid 
            ? Math.max(0, (sessionData.paidCount || 0) - occupancy) 
            : (sessionData.paidCount || 0),
          bookingIds: (sessionData.bookingIds || []).filter(id => id !== bookingId)
        });
      }
    }
    
    // Supprimer le booking
    await deleteDoc(bookingRef);
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression de la réservation:", error);
    throw error;
  }
}

// Mettre à jour le statut de paiement d'une réservation
// ============================================
// Marquer une réservation comme payée/non payée
// ============================================
// Paramètres :
//   bookingId : id de la réservation
//   isPaid : true = marquer comme soldée, false = annuler le paiement
//   paymentInfo (optionnel) : { method, balanceOnly }
//     - method : 'cash' | 'card_onsite' | 'check' | 'voucher' | undefined
//     - balanceOnly : true si on encaisse seulement le solde (cas acompte déjà payé)
// ============================================
export async function updateBookingPayment(bookingId, isPaid, paymentInfo = null) {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const bookingDoc = await getDoc(bookingRef);
    
    if (!bookingDoc.exists()) {
      throw new Error('Réservation non trouvée');
    }
    
    const bookingData = bookingDoc.data();
    const wasPaid = bookingData.isPaid;
    
    if (wasPaid === isPaid) return true; // Pas de changement
    
    // Préparer la mise à jour
    const updateFields = { isPaid };
    
    if (isPaid && paymentInfo) {
      // On encaisse : on stocke quand et comment
      updateFields.balancePaidAt = serverTimestamp();
      if (paymentInfo.method) {
        updateFields.balancePaymentMethod = paymentInfo.method;
      }
      // Si on encaisse seulement le solde (acompte déjà payé via Stripe)
      if (paymentInfo.balanceOnly) {
        updateFields.balanceEncashed = true;
      }
      // Le remainingAmount tombe à 0 puisque tout est payé
      updateFields.remainingAmount = 0;
    } else if (!isPaid) {
      // On annule le paiement : on retire les infos d'encaissement
      updateFields.balancePaidAt = null;
      updateFields.balancePaymentMethod = null;
      updateFields.balanceEncashed = false;
      // Si la réservation avait un acompte, le solde redevient ce qu'il était
      if (bookingData.depositPaid && bookingData.depositAmount) {
        updateFields.remainingAmount = Math.max(0, (bookingData.totalPrice || 0) - bookingData.depositAmount);
      }
    }
    
    // Mettre à jour le booking
    await updateDoc(bookingRef, updateFields);
    
    // Mettre à jour les sessions liées (paidCount)
    for (const slotTime of bookingData.slots) {
      const sessionsCol = collection(db, SESSIONS_COLLECTION);
      const q = query(
        sessionsCol, 
        where("date", "==", bookingData.date), 
        where("time", "==", slotTime)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const sessionDoc = snapshot.docs[0];
        const sessionData = sessionDoc.data();
        
        // Calculer la place effectivement prise par cette résa
        let occupancy = bookingData.slotOccupancy;
        if (occupancy === undefined || occupancy === null) {
          const formula = getFormulaById(bookingData.formulaId);
          occupancy = getEffectiveSlotOccupancy(formula, bookingData.nbParticipants);
        }
        
        // Important : si l'acompte avait déjà incrémenté paidCount, on ne le fait pas en double
        // Mais aujourd'hui le webhook NE met pas à jour paidCount pour un acompte (vérifié dans index.ts)
        // Donc on incrémente normalement quand on marque comme totalement payé
        await updateDoc(doc(db, SESSIONS_COLLECTION, sessionDoc.id), {
          paidCount: isPaid 
            ? (sessionData.paidCount || 0) + occupancy 
            : Math.max(0, (sessionData.paidCount || 0) - occupancy)
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du paiement:", error);
    throw error;
  }
}

// Mettre à jour les notes admin d'une réservation
export async function updateBookingNotes(bookingId, adminNotes) {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    await updateDoc(bookingRef, { adminNotes, updatedAt: serverTimestamp() });
    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour des notes:", error);
    throw error;
  }
}

// ============================================
// PARTICIPANTS (compatibilité ancienne version)
// ============================================

export async function getParticipants() {
  try {
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const snapshot = await getDocs(participantsCol);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des participants:", error);
    throw error;
  }
}

export async function getSessionParticipants(sessionId) {
  try {
    const participantsCol = collection(db, PARTICIPANTS_COLLECTION);
    const q = query(participantsCol, where("sessionId", "==", sessionId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des participants de la session:", error);
    throw error;
  }
}

export async function addParticipant(participantData) {
  try {
    const docRef = await addDoc(collection(db, PARTICIPANTS_COLLECTION), {
      ...participantData,
      createdAt: serverTimestamp()
    });
    
    const sessionRef = doc(db, SESSIONS_COLLECTION, participantData.sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: (sessionData.participantCount || 0) + 1,
        paidCount: participantData.paid ? (sessionData.paidCount || 0) + 1 : (sessionData.paidCount || 0)
      });
    }
    
    const newDoc = await getDoc(docRef);
    return { id: newDoc.id, ...newDoc.data() };
  } catch (error) {
    console.error("Erreur lors de l'ajout du participant:", error);
    throw error;
  }
}

export async function updateParticipant(participantId, data) {
  try {
    const participantRef = doc(db, PARTICIPANTS_COLLECTION, participantId);
    const participantDoc = await getDoc(participantRef);
    
    if (!participantDoc.exists()) {
      throw new Error('Participant non trouvé');
    }
    
    const oldData = participantDoc.data();
    await updateDoc(participantRef, data);
    
    if ('paid' in data && data.paid !== oldData.paid) {
      const sessionRef = doc(db, SESSIONS_COLLECTION, oldData.sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        await updateDoc(sessionRef, {
          paidCount: data.paid 
            ? (sessionData.paidCount || 0) + 1 
            : Math.max(0, (sessionData.paidCount || 0) - 1)
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du participant:", error);
    throw error;
  }
}

export async function deleteParticipant(participantId) {
  try {
    const participantRef = doc(db, PARTICIPANTS_COLLECTION, participantId);
    const participantDoc = await getDoc(participantRef);
    
    if (!participantDoc.exists()) {
      throw new Error('Participant non trouvé');
    }
    
    const participantData = participantDoc.data();
    const sessionId = participantData.sessionId;
    const wasPaid = participantData.paid;
    
    await deleteDoc(participantRef);
    
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      await updateDoc(sessionRef, {
        participantCount: Math.max(0, (sessionData.participantCount || 0) - 1),
        paidCount: wasPaid ? Math.max(0, (sessionData.paidCount || 0) - 1) : (sessionData.paidCount || 0)
      });
    }
    
    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression du participant:", error);
    throw error;
  }
}

// ============================================
// BLOCAGE / DÉBLOCAGE DE SESSIONS
// ============================================

export async function blockSession(sessionId, reason) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      isBlocked: true,
      blockReason: reason,
      participantCount: 0,
      paidCount: 0,
      bookingIds: []
    });
    
    // Supprimer les participants individuels de cette session
    const participants = await getSessionParticipants(sessionId);
    const deletePromises = participants.map(participant => 
      deleteDoc(doc(db, PARTICIPANTS_COLLECTION, participant.id))
    );
    await Promise.all(deletePromises);
    
    return true;
  } catch (error) {
    console.error("Erreur lors du blocage de la session:", error);
    throw error;
  }
}

export async function unblockSession(sessionId) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      isBlocked: false,
      blockReason: ''
    });
    return true;
  } catch (error) {
    console.error("Erreur lors du déblocage de la session:", error);
    throw error;
  }
}

// ============================================
// OUVERTURE / FERMETURE DE CRÉNEAUX
// ============================================

// Ouvrir un créneau individuel
export async function openSlot(sessionId) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, { isOpen: true });
    return true;
  } catch (error) {
    console.error("Erreur lors de l'ouverture du créneau:", error);
    throw error;
  }
}

// Fermer un créneau individuel
export async function closeSlot(sessionId) {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, { isOpen: false });
    return true;
  } catch (error) {
    console.error("Erreur lors de la fermeture du créneau:", error);
    throw error;
  }
}

// Ouvrir tous les créneaux d'un jour
// Crée les sessions si elles n'existent pas encore
export async function openAllSlotsForDay(dateStr) {
  try {
    const { ALL_SLOTS } = await import('../config/formulas');
    
    // Récupérer les sessions existantes pour ce jour
    const sessionsCol = collection(db, SESSIONS_COLLECTION);
    const q = query(sessionsCol, where("date", "==", dateStr));
    const snapshot = await getDocs(q);
    
    const existingSessions = {};
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      existingSessions[data.time] = { id: docSnap.id, ...data };
    });
    
    const promises = [];
    
    for (const time of ALL_SLOTS) {
      if (existingSessions[time]) {
        // Session existe → l'ouvrir
        promises.push(
          updateDoc(doc(db, SESSIONS_COLLECTION, existingSessions[time].id), { 
            isOpen: true 
          })
        );
      } else {
        // Session n'existe pas → la créer ouverte
        promises.push(
          addDoc(collection(db, SESSIONS_COLLECTION), {
            date: dateStr,
            time,
            isOpen: true,
            isBlocked: false,
            blockReason: '',
            participantCount: 0,
            paidCount: 0,
            bookingIds: [],
            createdAt: serverTimestamp()
          })
        );
      }
    }
    
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error("Erreur lors de l'ouverture du jour:", error);
    throw error;
  }
}

// Fermer tous les créneaux d'un jour
export async function closeAllSlotsForDay(dateStr) {
  try {
    const sessionsCol = collection(db, SESSIONS_COLLECTION);
    const q = query(sessionsCol, where("date", "==", dateStr));
    const snapshot = await getDocs(q);
    
    const promises = snapshot.docs.map(docSnap => 
      updateDoc(doc(db, SESSIONS_COLLECTION, docSnap.id), { isOpen: false })
    );
    
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error("Erreur lors de la fermeture du jour:", error);
    throw error;
  }
}

// ============================================
// CONTACTS (base de données clients)
// ============================================

// Sauvegarder ou mettre à jour un contact après une réservation
export async function saveContact(bookingData) {
  try {
    if (!bookingData.contactEmail && !bookingData.contactPhone) {
      return null; // Pas assez d'infos pour créer un contact
    }

    const contactsCol = collection(db, CONTACTS_COLLECTION);
    
    // Chercher si le contact existe déjà (par email ou téléphone)
    let existingContact = null;
    
    if (bookingData.contactEmail) {
      const emailQuery = query(contactsCol, where("email", "==", bookingData.contactEmail));
      const emailSnapshot = await getDocs(emailQuery);
      if (!emailSnapshot.empty) {
        existingContact = { id: emailSnapshot.docs[0].id, ...emailSnapshot.docs[0].data() };
      }
    }
    
    if (!existingContact && bookingData.contactPhone) {
      const phoneQuery = query(contactsCol, where("phone", "==", bookingData.contactPhone));
      const phoneSnapshot = await getDocs(phoneQuery);
      if (!phoneSnapshot.empty) {
        existingContact = { id: phoneSnapshot.docs[0].id, ...phoneSnapshot.docs[0].data() };
      }
    }

    if (existingContact) {
      // Mettre à jour le contact existant
      const contactRef = doc(db, CONTACTS_COLLECTION, existingContact.id);
      await updateDoc(contactRef, {
        name: bookingData.contactName || existingContact.name,
        email: bookingData.contactEmail || existingContact.email,
        phone: bookingData.contactPhone || existingContact.phone,
        totalBookings: (existingContact.totalBookings || 0) + 1,
        totalSpent: (existingContact.totalSpent || 0) + (bookingData.totalPrice || 0),
        lastBookingDate: bookingData.date,
        lastFormula: bookingData.formulaName,
        updatedAt: serverTimestamp()
      });
      return existingContact.id;
    } else {
      // Créer un nouveau contact
      const newContact = {
        name: bookingData.contactName || '',
        email: bookingData.contactEmail || '',
        phone: bookingData.contactPhone || '',
        totalBookings: 1,
        totalSpent: bookingData.totalPrice || 0,
        firstBookingDate: bookingData.date,
        lastBookingDate: bookingData.date,
        lastFormula: bookingData.formulaName || '',
        source: 'reservation_web',
        newsletter: true, // Opt-in par défaut (à vérifier RGPD)
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(contactsCol, newContact);
      return docRef.id;
    }
  } catch (error) {
    console.error("Erreur lors de la sauvegarde du contact:", error);
    // Ne pas faire échouer la réservation si le contact échoue
    return null;
  }
}

// Récupérer un contact par email
export async function getContactByEmail(email) {
  try {
    if (!email) return null;
    const contactsCol = collection(db, CONTACTS_COLLECTION);
    const q = query(contactsCol, where("email", "==", email));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
  } catch (error) {
    console.error("Erreur lors de la recherche du contact:", error);
    return null;
  }
}

// Récupérer tous les contacts
export async function getContacts() {
  try {
    const contactsCol = collection(db, CONTACTS_COLLECTION);
    const snapshot = await getDocs(contactsCol);
    return snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
      createdAt: docSnap.data().createdAt ? docSnap.data().createdAt.toDate() : null,
      updatedAt: docSnap.data().updatedAt ? docSnap.data().updatedAt.toDate() : null
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des contacts:", error);
    throw error;
  }
}

// Mettre à jour un contact (ex: opt-out newsletter)
export async function updateContact(contactId, data) {
  try {
    const contactRef = doc(db, CONTACTS_COLLECTION, contactId);
    await updateDoc(contactRef, { ...data, updatedAt: serverTimestamp() });
    return true;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du contact:", error);
    throw error;
  }
}

// Supprimer un contact (RGPD - droit à l'effacement)
export async function deleteContact(contactId) {
  try {
    await deleteDoc(doc(db, CONTACTS_COLLECTION, contactId));
    return true;
  } catch (error) {
    console.error("Erreur lors de la suppression du contact:", error);
    throw error;
  }
}

// ============================================
// CARTES CADEAUX
// ============================================

export async function createGiftCard(giftCardData) {
  try {
    const docRef = await addDoc(collection(db, 'giftcards'), {
      ...giftCardData,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  } catch (error) {
    console.error("Erreur création carte cadeau:", error);
    throw error;
  }
}

export async function getGiftCard(code) {
  try {
    const q = query(collection(db, 'giftcards'), where("code", "==", code));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  } catch (error) {
    console.error("Erreur recherche carte cadeau:", error);
    return null;
  }
}

export async function getGiftCards() {
  try {
    const snapshot = await getDocs(collection(db, 'giftcards'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Erreur récupération cartes cadeaux:", error);
    return [];
  }
}

// Supprimer une carte cadeau
export async function deleteGiftCard(giftCardId) {
  try {
    await deleteDoc(doc(db, 'giftcards', giftCardId));
    return true;
  } catch (error) {
    console.error("Erreur suppression carte cadeau:", error);
    throw error;
  }
}

export async function markGiftCardUsed(code, bookingId) {
  try {
    const q = query(collection(db, 'giftcards'), where("code", "==", code));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      await updateDoc(doc(db, 'giftcards', snapshot.docs[0].id), {
        status: 'used',
        usedAt: serverTimestamp(),
        usedForBooking: bookingId
      });
    }
    return true;
  } catch (error) {
    console.error("Erreur utilisation carte cadeau:", error);
    return false;
  }
}

// ============================================
// PARRAINAGES
// ============================================

export async function createReferral(referralData) {
  try {
    const docRef = await addDoc(collection(db, 'referrals'), {
      ...referralData,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id };
  } catch (error) {
    console.error("Erreur création parrainage:", error);
    throw error;
  }
}

export async function getReferralByCode(code) {
  try {
    const q = query(collection(db, 'referrals'), where("code", "==", code));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  } catch (error) {
    console.error("Erreur recherche parrainage:", error);
    return null;
  }
}

export async function getReferralsBySponsor(sponsorEmail) {
  try {
    const q = query(collection(db, 'referrals'), where("sponsorEmail", "==", sponsorEmail));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Erreur récupération parrainages:", error);
    return [];
  }
}

export async function markReferralUsed(code, usedByName) {
  try {
    const q = query(collection(db, 'referrals'), where("code", "==", code));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const ref = snapshot.docs[0];
      const data = ref.data();
      await updateDoc(doc(db, 'referrals', ref.id), {
        usedCount: (data.usedCount || 0) + 1,
        usedBy: [...(data.usedBy || []), { name: usedByName, date: new Date().toISOString() }]
      });
      // Créditer le parrain (5€ de crédit)
      if (data.sponsorEmail) {
        const contactQ = query(collection(db, 'contacts'), where("email", "==", data.sponsorEmail));
        const contactSnap = await getDocs(contactQ);
        if (!contactSnap.empty) {
          const contact = contactSnap.docs[0];
          const contactData = contact.data();
          await updateDoc(doc(db, 'contacts', contact.id), {
            referralCredit: (contactData.referralCredit || 0) + 5,
            updatedAt: serverTimestamp()
          });
        }
      }
    }
    return true;
  } catch (error) {
    console.error("Erreur utilisation parrainage:", error);
    return false;
  }
}

// Marquer l'email de la carte cadeau comme envoyé
export async function updateGiftCardEmailSent(giftCardId) {
  try {
    await updateDoc(doc(db, 'giftcards', giftCardId), {
      emailPending: false,
      emailSentAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error("Erreur mise à jour email carte cadeau:", error);
    return false;
  }
}

// ============================================
// MIGRATION : recalculer slotOccupancy d'une résa existante
// ============================================
// Utilité : pour les résas créées AVANT que la logique de privatisation
// n'existe (anciennes résas en base), cette fonction recalcule
// l'occupation correcte du créneau et met à jour les sessions liées.
//
// À utiliser ponctuellement depuis l'admin quand on détecte un cas
// (ex: une résa de groupe qui n'avait pas privatisé l'arène).
// ============================================
export async function recalculateBookingSlotOccupancy(bookingId) {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId);
    const bookingDoc = await getDoc(bookingRef);
    
    if (!bookingDoc.exists()) {
      throw new Error('Réservation non trouvée');
    }
    
    const bookingData = bookingDoc.data();
    
    // Si annulée, on ne touche à rien
    if (bookingData.status === 'cancelled') {
      return { ok: false, reason: 'Réservation annulée' };
    }
    
    // Calculer l'occupation correcte
    const formula = getFormulaById(bookingData.formulaId);
    const correctOccupancy = getEffectiveSlotOccupancy(formula, bookingData.nbParticipants);
    
    // Occupation actuellement enregistrée (ou par défaut nbParticipants comme avant)
    const currentOccupancy = bookingData.slotOccupancy !== undefined 
      ? bookingData.slotOccupancy 
      : (bookingData.nbParticipants || 1);
    
    // Si elles sont identiques, rien à faire
    if (correctOccupancy === currentOccupancy) {
      // On profite quand même pour stocker slotOccupancy si manquant
      if (bookingData.slotOccupancy === undefined) {
        await updateDoc(bookingRef, { slotOccupancy: correctOccupancy });
      }
      return { ok: true, changed: false, occupancy: correctOccupancy };
    }
    
    // Ajustement = différence à appliquer aux sessions
    const delta = correctOccupancy - currentOccupancy;
    
    // Mettre à jour le booking
    const isPrivative = correctOccupancy === MAX_PLAYERS_PER_SLOT && formula?.category !== 'individual';
    await updateDoc(bookingRef, { 
      slotOccupancy: correctOccupancy,
      isPrivative: isPrivative
    });
    
    // Ajuster chaque session liée
    if (bookingData.slots && bookingData.date) {
      for (const slotTime of bookingData.slots) {
        const sessionsCol = collection(db, SESSIONS_COLLECTION);
        const q = query(
          sessionsCol, 
          where("date", "==", bookingData.date), 
          where("time", "==", slotTime)
        );
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const sessionDoc = snapshot.docs[0];
          const sessionData = sessionDoc.data();
          
          const newCount = Math.max(0, (sessionData.participantCount || 0) + delta);
          const newPaidCount = bookingData.isPaid 
            ? Math.max(0, (sessionData.paidCount || 0) + delta)
            : (sessionData.paidCount || 0);
          
          await updateDoc(doc(db, SESSIONS_COLLECTION, sessionDoc.id), {
            participantCount: newCount,
            paidCount: newPaidCount
          });
        }
      }
    }
    
    return { 
      ok: true, 
      changed: true, 
      occupancy: correctOccupancy,
      previousOccupancy: currentOccupancy,
      delta
    };
  } catch (error) {
    console.error('Erreur recalcul slotOccupancy:', error);
    throw error;
  }
}
